import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { of, Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorHandlingService } from '../../services/error-handling/error-handling.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { HttpHeaderConstants } from '../../services/http-header.constant';
import { EloquaInstall } from '../../models/appcloud/eloqua-install.model';
import { EloquaInstallError } from '../../models/eloqua/eloqua-install-error.model';

@Injectable({
    providedIn: 'root',
})
export class EloquaInstallService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private ELOQUAINSTALLS_URL: string = environment.publicApiHost + '/appcloud/eloquainstalls';

    getByInstallId(installId: string, additionalHeaders: any = {}): Observable<EloquaInstall[]> {
        this.#spinnerService.addProcess(
            'EloquaInstallService.getByInstallId.' + installId,
            'Loading your configuration...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('installId', installId),
        };

        return this.#http.get<EloquaInstall[]>(this.ELOQUAINSTALLS_URL, httpOptions).pipe(
            catchError((error: EloquaInstallError) => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving app data from Relationship One. Please try again later.'
                );
                return throwError(error);
            }),
            finalize(() => this.#spinnerService.completeProcess('EloquaInstallService.getByInstallId.' + installId))
        );
    }

    update(id: number, eloquaInstall: EloquaInstall, additionalHeaders: any = {}) {
        this.#spinnerService.addProcess('EloquaInstallService.update.' + id, 'Updating your configuration...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http.patch<EloquaInstall>(this.ELOQUAINSTALLS_URL + '/' + id, eloquaInstall, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while sending data to Relationship One. Please try again later.'
                );
                return of<EloquaInstall>();
            }),
            finalize(() => this.#spinnerService.completeProcess('EloquaInstallService.update.' + id))
        );
    }
}
